import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Search, ArrowRight, Euro, Maximize, Bed, Sofa, Droplets, Check, ChevronDown, ChevronUp, SlidersHorizontal, Trees, Car, Sun, Sparkles, Home } from 'lucide-react';
import Button from './ui/Button';
import CitySelector from './CitySelector';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '../lib/analytics';
import slide1 from '../assets/slide_1.jpg';
import slide2 from '../assets/slide_2.jpg';
import slide3 from '../assets/slide_3.jpg';

const Sparkle = ({ size, top, left, delay, duration = 3 }) => (
    <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{
            scale: [0, 1.2, 0],
            opacity: [0, 0.8, 0],
            rotate: [0, 45, 0]
        }}
        transition={{
            duration: duration,
            repeat: Infinity,
            delay: delay,
            ease: "easeInOut"
        }}
        className="absolute pointer-events-none z-20"
        style={{ top: `${top}%`, left: `${left}%` }}
    >
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" fill="white" fillOpacity="0.8" />
        </svg>
    </motion.div>
);

const SearchHero = ({
    activeStatus, setActiveStatus,
    activeType, setActiveType,
    activeCities, setActiveCities,
    activeBudget, setActiveBudget,
    activeMinSurface, setActiveMinSurface,
    activeMinRooms, setActiveMinRooms,
    activeFeatures, setActiveFeatures,
    cities, cityCounts, loading,
    resultsCount, isFilterActive,
    hasRentals = false
}) => {
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const slides = [slide1, slide2, slide3];

    React.useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const navigate = useNavigate();

    const toggleFeature = (feat) => {
        if (activeFeatures.includes(feat)) {
            setActiveFeatures(activeFeatures.filter(f => f !== feat));
        } else {
            setActiveFeatures([...activeFeatures, feat]);
        }
    };

    const countActiveAdvanced = () => {
        let count = 0;
        if (activeMinSurface) count++;
        if (activeMinRooms) count++;
        count += activeFeatures.length;
        return count;
    };

    return (
        <div className="relative md:h-[85vh] md:min-h-[700px] min-h-[90svh] h-auto flex items-start justify-center pt-12 md:pt-24 z-30 pb-12 md:pb-0">
            {/* Background Slider */}
            <div className="absolute top-0 left-0 w-full h-[120vh] md:inset-0 md:h-full z-0 overflow-hidden">
                <AnimatePresence mode="manual">
                    <motion.div
                        key={slides[currentSlide]}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 2, ease: "easeInOut" }}
                        className="absolute inset-0"
                    >
                        <div
                            className="absolute inset-0 bg-cover bg-center md:bg-fixed"
                            style={{ backgroundImage: `url(${slides[currentSlide]})` }}
                        />
                    </motion.div>
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-b from-[#2C353D]/80 via-[#2C353D]/40 to-white"></div>
                {/* Animated floating particles */}
                <motion.div animate={{ y: [-10, 10, -10] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-[15%] left-[8%] w-3 h-3 bg-[#EA723D]/40 rounded-full blur-sm" />
                <motion.div animate={{ y: [8, -12, 8] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }} className="absolute top-[30%] left-[75%] w-5 h-5 bg-[#EA723D]/30 rounded-full blur-md" />
                <motion.div animate={{ y: [-6, 14, -6] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }} className="absolute top-[55%] left-[20%] w-2 h-2 bg-white/30 rounded-full" />
                <motion.div animate={{ y: [10, -8, 10] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }} className="absolute top-[20%] left-[55%] w-4 h-4 bg-white/20 rounded-full blur-sm" />
                <motion.div animate={{ y: [-12, 6, -12] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }} className="absolute top-[70%] left-[85%] w-3 h-3 bg-[#EA723D]/25 rounded-full blur-sm" />
                <Sparkle size={40} top={80} left={90} delay={3} duration={7} />
            </div>

            <div className="relative z-10 w-full max-w-6xl px-4 flex flex-col items-center text-center pt-16 md:pt-0">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="relative"
                >
                    <Sparkle size={12} top={-40} left={15} delay={0.5} duration={4} />
                    <Sparkle size={18} top={-20} left={80} delay={1.2} duration={5} />
                    <Sparkle size={10} top={20} left={90} delay={2.1} duration={3} />
                    <Sparkle size={14} top={110} left={5} delay={0.8} duration={4.5} />

                    <div className="relative inline-block overflow-hidden rounded-full mb-6">
                        <span className="inline-block py-1.5 px-6 rounded-full bg-[#EA723D] text-white text-[10px] font-bold tracking-[0.2em] relative z-10 shadow-lg shadow-orange-900/40 italic">
                            L'EXCELLENCE IMMOBILIÈRE
                        </span>
                        <motion.div
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 3, repeat: Infinity, delay: 1, ease: "slow" }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent z-0"
                        />
                    </div>
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-normal text-white mb-8 leading-tight drop-shadow-2xl">
                        <motion.span
                            animate={{ opacity: [0.9, 1, 0.9] }}
                            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                            className="inline-block"
                        >
                            Votre Agence Immobilière
                        </motion.span>
                        <br />
                        <motion.span
                            animate={{ textShadow: ['0 0 20px rgba(234,114,61,0)', '0 0 30px rgba(234,114,61,0.5)', '0 0 20px rgba(234,114,61,0)'] }}
                            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                            className="text-[#EA723D] italic relative inline-block"
                        >
                            du Secteur Frontalier
                            <Sparkle size={25} top={-20} left={90} delay={0.3} duration={4} />
                        </motion.span>
                        <span className="block text-lg md:text-2xl mt-3 font-semibold text-white/80 tracking-wide">Frontière Luxembourg • Moselle • Meuse • Meurthe-et-Moselle</span>
                    </h1>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    className="w-full max-w-5xl"
                >
                    <div className="flex justify-center mb-0 relative z-20">
                        <div
                            className="bg-white px-8 py-4 rounded-t-2xl text-sm font-semibold tracking-wider text-[#2C353D] border-t border-x border-white relative"
                        >
                            TROUVER UN BIEN
                            <Sparkle size={15} top={50} left={105} delay={1.8} duration={4} />
                        </div>
                    </div>

                    <div className="bg-white rounded-b-3xl rounded-tr-3xl rounded-tl-none p-6 md:p-8 shadow-2xl relative z-30 min-h-[200px] flex items-start">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key="buy"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="flex flex-col w-full"
                            >
                                {/* ── Filtres principaux ── */}
                                <div className="grid grid-cols-1 min-[598px]:grid-cols-2 md:grid-cols-4 gap-4 items-end w-full">
                                    <div className="w-full text-left">
                                        <label htmlFor="projet-select" className="block text-[#2C353D] text-xs font-semibold uppercase tracking-wider mb-2 ml-1">Projet</label>
                                        <div className="relative group">
                                            <Home className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA723D] transition-colors" size={20} aria-hidden="true" />
                                            <select
                                                id="projet-select"
                                                value={activeStatus}
                                                onChange={(e) => setActiveStatus(e.target.value)}
                                                className="w-full pl-12 pr-10 py-4 bg-white border-2 border-gray-100 rounded-xl text-gray-800 text-sm font-medium focus:outline-none focus:border-[#EA723D] hover:border-gray-200 transition-all appearance-none cursor-pointer"
                                            >
                                                {hasRentals && <option value="Tous">Acheter &amp; Louer</option>}
                                                <option value="Vente">Acheter</option>
                                                {hasRentals && <option value="Location">Louer</option>}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="w-full text-left">
                                        <label htmlFor="type-select" className="block text-[#2C353D] text-xs font-semibold uppercase tracking-wider mb-2 ml-1">Type de bien</label>
                                        <div className="relative group">
                                            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA723D] transition-colors" size={20} aria-hidden="true" />
                                            <select
                                                id="type-select"
                                                value={activeType}
                                                onChange={(e) => setActiveType(e.target.value)}
                                                className="w-full pl-12 pr-10 py-4 bg-white border-2 border-gray-100 rounded-xl text-gray-800 text-sm font-medium focus:outline-none focus:border-[#EA723D] hover:border-gray-200 transition-all appearance-none cursor-pointer"
                                            >
                                                <option value="Tous">Tout voir</option>
                                                <option value="Maison">Maison</option>
                                                <option value="Appartement">Appartement</option>
                                                <option value="Terrain">Terrain</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="w-full text-left">
                                        <label className="block text-[#2C353D] text-xs font-semibold uppercase tracking-wider mb-2 ml-1">Localisation</label>
                                        <CitySelector selectedCities={activeCities} onChange={setActiveCities} cityCounts={cityCounts} loading={loading} availableCities={cities} />
                                    </div>

                                    <div className="w-full text-left">
                                        <label className="block text-[#2C353D] text-xs font-bold uppercase tracking-wider mb-2 ml-1">
                                            {activeStatus === 'Location' ? 'Budget Max / Mois' : 'Budget Max'}
                                        </label>
                                        <div className="relative group">
                                            {activeStatus !== 'Location' && (
                                                <Euro className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA723D] transition-colors" size={20} />
                                            )}
                                            {activeStatus === 'Location' ? (
                                                <div className="w-full h-[58px] pl-4 pr-3 bg-white border border-gray-200 rounded-xl flex items-center gap-3">
                                                    <div className="flex-1 flex items-center">
                                                        <input
                                                            type="range"
                                                            min="300"
                                                            max="2000"
                                                            step="50"
                                                            value={activeBudget || 2000}
                                                            onChange={(e) => setActiveBudget(e.target.value === "2000" ? "" : e.target.value)}
                                                            className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#EA723D] transition-all touch-none"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-1 bg-[#F4F7FA] px-2 py-1.5 rounded-lg border border-gray-100 group-focus-within:border-[#EA723D]/30 transition-all">
                                                        <input
                                                            type="number"
                                                            value={activeBudget}
                                                            onChange={(e) => setActiveBudget(e.target.value)}
                                                            placeholder="2000"
                                                            className="w-11 bg-transparent text-[#2C353D] font-bold text-sm outline-none text-right [appearance:textfield]"
                                                        />
                                                        <span className="text-[#2C353D]/50 font-bold text-xs">€</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <select
                                                    value={activeBudget}
                                                    onChange={(e) => setActiveBudget(e.target.value)}
                                                    className="w-full h-[58px] pl-12 pr-4 bg-white border border-gray-200 rounded-xl text-[#2C353D] font-bold focus:ring-2 focus:ring-[#EA723D]/20 focus:border-[#EA723D] outline-none transition-all appearance-none cursor-pointer hover:bg-gray-50"
                                                >
                                                    <option value="">Budget illimité</option>
                                                    <option value="100000">100 000 €</option>
                                                    <option value="150000">150 000 €</option>
                                                    <option value="200000">200 000 €</option>
                                                    <option value="300000">300 000 €</option>
                                                    <option value="400000">400 000 €</option>
                                                    <option value="500000">500 000 €</option>
                                                    <option value="700000">700 000 €</option>
                                                    <option value="1000000">1 000 000 €</option>
                                                </select>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bouton filtres avancés */}
                                    <div className="min-[598px]:col-span-2 md:col-span-4 flex justify-center mt-2 mb-1">
                                        <button
                                            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                                            className="flex items-center gap-2 text-gray-500 hover:text-[#2C353D] font-bold text-sm transition-colors py-2 px-4 rounded-full hover:bg-gray-100"
                                        >
                                            <SlidersHorizontal size={16} />
                                            Filtres avancés
                                            {countActiveAdvanced() > 0 && (
                                                <span className="bg-[#EA723D] text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full ml-1">
                                                    {countActiveAdvanced()}
                                                </span>
                                            )}
                                            {isAdvancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Bouton résultats */}
                                <AnimatePresence>
                                    {isFilterActive && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            className="mt-6 flex justify-center"
                                        >
                                            <button
                                                onClick={() => {
                                                    setIsAdvancedOpen(false);
                                                    trackEvent('search', {
                                                        search_term: `${activeStatus} ${activeType} in ${activeCities.join(', ')}`,
                                                        results_count: resultsCount
                                                    });
                                                    setTimeout(() => {
                                                        document.getElementById('results')?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 320);
                                                }}
                                                className="bg-[#2C353D] text-white px-8 py-3 rounded-xl font-bold text-sm shadow-xl shadow-gray-900/30 flex items-center gap-3 hover:bg-[#3D4852] transition-all hover:translate-y-[-2px]"
                                            >
                                                <Search size={18} />
                                                Afficher les {resultsCount} biens correspondants
                                                <ArrowRight size={18} />
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* ── Filtres avancés ── */}
                                <AnimatePresence>
                                    {isAdvancedOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1, transition: { duration: 0.3, ease: 'easeInOut' } }}
                                            exit={{ height: 0, opacity: 0, transition: { duration: 0.3, ease: 'easeInOut' } }}
                                            className="overflow-hidden w-full"
                                        >
                                            {/* Ligne 1 : Surface + Chambres */}
                                            <div className="flex flex-wrap gap-4 pt-2 mt-2">
                                                <div className="text-left min-w-[140px] flex-1">
                                                    <label className="block text-[#2C353D] text-[10px] font-bold uppercase tracking-wider mb-2">Surface Min</label>
                                                    <div className="relative">
                                                        <Maximize className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                                                        <input
                                                            type="number"
                                                            value={activeMinSurface}
                                                            onChange={(e) => setActiveMinSurface(e.target.value)}
                                                            className="w-full h-11 pl-9 pr-7 bg-white border border-gray-200 rounded-lg text-sm text-[#2C353D] font-bold outline-none focus:border-[#EA723D] transition-all"
                                                        />
                                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-[10px]">m²</span>
                                                    </div>
                                                </div>
                                                <div className="text-left min-w-[130px] flex-1">
                                                    <label className="block text-[#2C353D] text-[10px] font-bold uppercase tracking-wider mb-2">Chambres min</label>
                                                    <div className="relative">
                                                        <Bed className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                                                        <select
                                                            value={activeMinRooms}
                                                            onChange={(e) => setActiveMinRooms(e.target.value)}
                                                            className="w-full h-11 pl-9 pr-4 bg-white border border-gray-200 rounded-lg text-sm text-[#2C353D] font-bold outline-none focus:border-[#EA723D] appearance-none cursor-pointer"
                                                        >
                                                            <option value="">Indiff.</option>
                                                            <option value="1">1+</option>
                                                            <option value="2">2+</option>
                                                            <option value="3">3+</option>
                                                            <option value="4">4+</option>
                                                            <option value="5">5+</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Ligne 2 : 4 critères principaux avec emoji */}
                                            <div className="mt-4">
                                                <label className="block text-[#2C353D] text-[10px] font-bold uppercase tracking-wider mb-2">Caractéristiques</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {[
                                                        { id: 'Jardin',       label: 'Jardin',       Icon: Trees    },
                                                        { id: 'Garage',       label: 'Garage',       Icon: Car      },
                                                        { id: 'Terrasse',     label: 'Terrasse',     Icon: Sun      },
                                                        { id: 'Sans travaux', label: 'Sans travaux', Icon: Sparkles },
                                                    ].map(({ id, label, Icon }) => (
                                                        <button
                                                            key={id}
                                                            onClick={() => toggleFeature(id)}
                                                            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all border flex items-center gap-2 ${activeFeatures.includes(id)
                                                                ? 'bg-[#2C353D] border-[#2C353D] text-white'
                                                                : 'bg-white border-gray-200 text-gray-600 hover:border-[#2C353D] hover:text-[#2C353D]'
                                                                }`}
                                                        >
                                                            <Icon size={14} />
                                                            {label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Ligne 3 : critères secondaires sans emoji */}
                                            <div className="mt-3 mb-2">
                                                <label className="block text-[#2C353D] text-[10px] font-bold uppercase tracking-wider mb-2">Critères spécifiques</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {[
                                                        'Ascenseur', 'Balcon', 'Buanderie', 'Cave', 'Climatisation',
                                                        'Combles aménageables', 'Cour extérieure', 'Grenier', 'Parking',
                                                        'Piscine', 'Sous-sol', 'Véranda', 'Rénové',
                                                    ].map((id) => (
                                                        <button
                                                            key={id}
                                                            onClick={() => toggleFeature(id)}
                                                            className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all border ${activeFeatures.includes(id)
                                                                ? 'bg-[#2C353D] border-[#2C353D] text-white'
                                                                : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'
                                                                }`}
                                                        >
                                                            {id}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default SearchHero;
